import { useState, useEffect, useRef, useCallback } from "react";
// @ts-ignore — react-dom is a peer dependency
import { createPortal } from "react-dom";
import { SUPPORTED_LOCALES, LOCALE_LABELS } from "../../i18n";
import { GlobeIcon, CheckIcon, ChevronDownIcon } from "../icons";

export function LangSwitcher({
  activeLocale: current,
  onSelect,
  portalContainer,
}: {
  activeLocale: string;
  onSelect: (code: string) => void;
  portalContainer?: Element | DocumentFragment;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const currentLabel = LOCALE_LABELS[current] || "English";

  // Calculate dropdown position from trigger button
  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    updatePos();
    const handler = (e: MouseEvent) => {
      const path = e.composedPath?.() ?? [];
      if (triggerRef.current && path.includes(triggerRef.current)) return;
      if (dropRef.current && path.includes(dropRef.current)) return;
      if (triggerRef.current?.contains(e.target as Node)) return;
      if (dropRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      document.removeEventListener("mousedown", handler);
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [open, updatePos]);

  return (
    <>
      <button
        ref={triggerRef}
        className={`ps-tryon-lang-trigger${open ? " ps-active" : ""}`}
        onClick={() => setOpen(!open)}
      >
        <GlobeIcon size={15} />
        <span className="ps-tryon-lang-current">{currentLabel}</span>
        <span className={`ps-tryon-lang-arrow${open ? " ps-open" : ""}`}>
          <ChevronDownIcon size={18} />
        </span>
      </button>
      {open && createPortal(
        <div
          ref={dropRef}
          className="ps-tryon-lang-dropdown"
          style={{
            position: "fixed",
            top: pos.top,
            right: pos.right,
            // Match the overlay's z-index (2147483647) so the dropdown sits
            // above it. With equal z-index, document order wins — and the
            // dropdown is portaled later than the overlay so it stacks on top.
            zIndex: 2147483647,
          }}
        >
          <div className="ps-tryon-lang-list">
            {SUPPORTED_LOCALES.map(code => (
              <button
                key={code}
                className={`ps-tryon-lang-item${code === current ? " ps-selected" : ""}`}
                onClick={() => { onSelect(code); setOpen(false); }}
              >
                <span className="ps-tryon-lang-name">{LOCALE_LABELS[code] || code}</span>
                <span className="ps-tryon-lang-code">{code.toUpperCase()}</span>
                {code === current && <span className="ps-tryon-lang-check"><CheckIcon size={12} /></span>}
              </button>
            ))}
          </div>
        </div>,
        portalContainer ?? document.body
      )}
    </>
  );
}
